import { Component, EventEmitter, HostListener, Input, OnDestroy, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { GeneralService } from 'src/app/services/general.service';
import { SocketWebRtcService } from 'src/app/services/socket-web-rtc.service';
import { ZoomService } from 'src/app/services/zoom.service';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment.dev';

export type MediaPermissionErrorType =
  | 'PERMISSION_DENIED'
  | 'DEVICE_NOT_FOUND'
  | 'DEVICE_BUSY'
  | 'CONSTRAINT_ERROR'
  | 'NOT_SUPPORTED'
  | 'SECURITY_ERROR'
  | 'UNKNOWN';

export interface MediaPermissionErrorPayload {
  ok: false;
  type: MediaPermissionErrorType;
  code: string;
  detail: string;
  name?: string;
  message?: string;
  roomId?: string | number;
  userId?: string | number;
  ts: string; // ISO
}

@Component({
  selector: 'app-video-web-rtc',
  templateUrl: './video-web-rtc.component.html',
  styleUrls: ['./video-web-rtc.component.css']
})
export class VideoWebRtcComponent implements OnDestroy, OnChanges {

  @Input() video_iniciado: boolean = false;
  @Input() video_finalizada: boolean = false;
  @Input() selectedView: string | null = null;

  @Output() toggleView = new EventEmitter<void>();
  @Output() connectionLog = new EventEmitter<string>();

  // ✅ NUEVO: para que el componente padre sepa del error (si está en la misma vista)
  @Output() mediaPermissionError = new EventEmitter<MediaPermissionErrorPayload>();

  public consulta: any = {};
  public usuario: any = {};
  public muteFlag = false;
  public hideCameraFlag = false;
  public isExpanded = false;
  public userVideoList: any[] = [];
  public userVideoSelected: any = '';

  public isVideoView = false;

  private iceServers: RTCConfiguration = environment.WEBRTC_ICE_SERVERS;

  private isStartMeeting: boolean = false;
  private urlParams: any = {};

  private codecs_audio: any = {};
  private codecs_video: any = {};
  private codecs_audio_mimeType: any = {};
  private codecs_video_mimeType: any = {};

  public enterSound: any = new Audio('assets/sounds/enter.mp3');
  public leaveSound: any = new Audio('assets/sounds/leave.mp3');

  // Para evitar logs repetidos
  private patientConnectedLogged = false;
  private patientDisconnectedLogged = false;

  // Para timeout de “no se conectó”
  private patientSignalTs: number | null = null;
  private patientConnectTimeoutRef: any = null;

  constructor(
    private _zoomService: ZoomService,
    public web_rtc_socket: SocketWebRtcService,
    private route: ActivatedRoute,
    private router: Router,
    private general_services: GeneralService
  ) {
    this.usuario = new EncriptacionService().getUserInfo();

    this.codecs_audio = RTCRtpReceiver.getCapabilities('audio').codecs;
    this.codecs_video = RTCRtpReceiver.getCapabilities('video').codecs;

    this.codecs_audio_mimeType = this.getUniqueListBy(this.codecs_audio, 'mimeType');
    this.codecs_video_mimeType = this.getUniqueListBy(this.codecs_video, 'mimeType');

    // Señalización clásica
    this.web_rtc_socket.joined().subscribe(data => this.joined(data));
    this.web_rtc_socket.created().subscribe(data => this.created(data));
    this.web_rtc_socket.full().subscribe(data => this.full(data));
    this.web_rtc_socket.ready().subscribe(data => this.ready(data));
    this.web_rtc_socket.candidate().subscribe(data => this.candidate(data));
    this.web_rtc_socket.answer().subscribe(data => this.answer(data));
    this.web_rtc_socket.leave().subscribe(data => this.leave(data));
    this.web_rtc_socket.offer().subscribe(data => this.offer(data));
    this.web_rtc_socket.participants().subscribe(data => this.participants(data));
    this.web_rtc_socket.endMeetingByDoctor().subscribe(data => this.endMeetingByDoctor(data));
    this.web_rtc_socket.isNotCompatible().subscribe(data => this.isNotCompatible(data));
    this.web_rtc_socket.isNotAuthorized().subscribe(data => this.isNotAuthorized(data));

    // ✅ NUEVO: recibir estados del paciente (reemitidos por backend)
    if ((this.web_rtc_socket as any).webrtcUserStatus) {
      (this.web_rtc_socket as any).webrtcUserStatus().subscribe((payload: any) => {
        try {
          const soyDoctor = this.usuario?.typ_id === 1;
          const esPaciente = payload?.userTypeId === 2;

          if (soyDoctor && esPaciente && payload?.roomName === this.consulta?.id_consulta) {
            this.markPatientSignal();
            this.logConnection(`PACIENTE: ${payload.code}${payload.detail ? ' - ' + payload.detail : ''}`);

            if (payload.code === 'PATIENT_WEBRTC_CONNECTED' && !this.patientConnectedLogged) {
              this.patientConnectedLogged = true;
              this.logConnection('✅ PACIENTE CONECTADO a la llamada (según estados reportados).');
            }

            // También considera permisos
            if (
              payload.code === 'PATIENT_MEDIA_DENIED' ||
              payload.code === 'PATIENT_NO_DEVICE' ||
              payload.code === 'PATIENT_MEDIA_IN_USE' ||
              payload.code === 'PATIENT_MEDIA_BLOCKED'
            ) {
              this.logConnection(`⚠️ PACIENTE tuvo un problema con cámara/micrófono: ${payload.code}`);
            }

            if (payload.code === 'PATIENT_LEFT' && !this.patientDisconnectedLogged) {
              this.patientDisconnectedLogged = true;
              this.logConnection('⚠️ PACIENTE SALIÓ de la llamada (según estados reportados).');
            }
          }
        } catch (e) {
          console.warn('Error procesando webrtcUserStatus:', e);
        }
      });
    }

    this.route.url.subscribe((params: any) => {
      this.urlParams = params[0];
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedView']) {
      this.isVideoView = this.selectedView === 'video';
    }
  }

  // ---------------------------
  // LOGS + REPORTES
  // ---------------------------
  private logConnection(message: string) {
    const rol =
      this.usuario?.typ_id === 1 ? 'DOCTOR' :
      this.usuario?.typ_id === 2 ? 'PACIENTE' :
      'DESCONOCIDO';

    const msg = `[${rol}] ${message} (consulta: ${this.consulta?.id_consulta || 'N/A'})`;
    // console.log(msg);
    this.connectionLog.emit(msg);
  }

  private reportStatus(code: string, detail?: string) {
    // Solo reportamos estados del paciente (para que el doctor vea que pasó)
    if (this.usuario?.typ_id !== 2) return;

    const payload = {
      roomName: this.consulta?.id_consulta,
      userTypeId: this.usuario?.typ_id, // 2
      userId: this.usuario?._id,
      code,
      detail: detail || null,
      ts: new Date().toISOString()
    };

    this.logConnection(`${code}${detail ? ' - ' + detail : ''}`);
    this.web_rtc_socket.socketEmit('webrtcUserStatus', payload, null);
  }

  private mapGetUserMediaError(err: any): { code: string; detail: string; type: MediaPermissionErrorType } {
    const name = (err?.name || 'UnknownError').toString();

    switch (name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return {
          code: 'PATIENT_MEDIA_DENIED',
          detail: 'El paciente denegó permisos de cámara/micrófono.',
          type: 'PERMISSION_DENIED'
        };

      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return {
          code: 'PATIENT_NO_DEVICE',
          detail: 'No se encontraron dispositivos de cámara/micrófono.',
          type: 'DEVICE_NOT_FOUND'
        };

      case 'NotReadableError':
      case 'TrackStartError':
        return {
          code: 'PATIENT_MEDIA_IN_USE',
          detail: 'El dispositivo está en uso o no se puede leer.',
          type: 'DEVICE_BUSY'
        };

      case 'SecurityError':
        return {
          code: 'PATIENT_MEDIA_BLOCKED',
          detail: 'Bloqueado por seguridad (HTTPS/políticas del navegador).',
          type: 'SECURITY_ERROR'
        };

      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return {
          code: 'PATIENT_MEDIA_BLOCKED',
          detail: 'Restricción de constraints (resolución/fps no disponible).',
          type: 'CONSTRAINT_ERROR'
        };

      case 'NotSupportedError':
        return {
          code: 'PATIENT_MEDIA_BLOCKED',
          detail: 'getUserMedia no soportado en este navegador/dispositivo.',
          type: 'NOT_SUPPORTED'
        };

      default:
        return {
          code: 'PATIENT_MEDIA_BLOCKED',
          detail: `Error inesperado: ${name}`,
          type: 'UNKNOWN'
        };
    }
  }

  private emitMediaPermissionError(err: any) {
    const mapped = this.mapGetUserMediaError(err);

    const payload: MediaPermissionErrorPayload = {
      ok: false,
      type: mapped.type,
      code: mapped.code,
      detail: mapped.detail,
      name: err?.name,
      message: err?.message,
      roomId: this.consulta?.id_consulta,
      userId: this.usuario?._id,
      ts: new Date().toISOString()
    };

    // Output al padre (si lo escuchas)
    this.mediaPermissionError.emit(payload);

    // Para logs/doctor (si el backend reemite)
    this.reportStatus(mapped.code, mapped.detail);

    // SessionStorage para mostrar modal en otra vista (si lo necesitas)
    sessionStorage.setItem('webrtc_media_error', mapped.code);
    sessionStorage.setItem('webrtc_media_error_detail', mapped.detail || '');
  }

  private markPatientSignal() {
    this.patientSignalTs = Date.now();
  }

  private startPatientNoConnectTimeout(ms: number = 40000) {
    // Solo interesa en DOCTOR
    if (this.usuario?.typ_id !== 1) return;

    if (this.patientConnectTimeoutRef) {
      clearTimeout(this.patientConnectTimeoutRef);
      this.patientConnectTimeoutRef = null;
    }

    const startedAt = Date.now();
    this.patientConnectTimeoutRef = setTimeout(() => {
      const gotSignal = this.patientSignalTs && this.patientSignalTs >= startedAt;
      if (!gotSignal) {
        this.logConnection('❌ PACIENTE NO SE CONECTÓ: no hubo señales en 40s (posible no ingresó o quedó bloqueado por permisos/red).');
      }
    }, ms);
  }

  // ---------------------------
  // UTILIDADES
  // ---------------------------
  getUniqueListBy(arr: any[], key: string) {
    return [...new Map(arr.map(item => [item[key], item])).values()];
  }

  // ---------------------------
  // ENTRADA A SALA
  // ---------------------------
  joinRoom(consult: any) {
    this.consulta = consult;
    this.userVideoList = [];
    this.patientConnectedLogged = false;
    this.patientDisconnectedLogged = false;
    this.patientSignalTs = null;

    if (this.usuario?.typ_id === 1) {
      this.startPatientNoConnectTimeout(40000);
    }

    if (this.usuario?.typ_id === 2) {
      this.reportStatus('PATIENT_JOIN_ROOM_SENT', 'El paciente intentó unirse a la sala.');
    }

    const params = {
      roomName: consult.id_consulta,
      token: sessionStorage.getItem('token')
    };
    this.web_rtc_socket.joinRoom(params);
  }

  // ---------------------------
  // JOINED / CREATED (getUserMedia)
  // ---------------------------
  joined(data: any) {
    try {
      navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        .then(stream => {
          if (this.usuario?.typ_id === 2) {
            stream.getVideoTracks()[0].enabled = false;
            stream.getAudioTracks()[0].enabled = false;
            this.hideCameraFlag = true;
            this.muteFlag = true;
            this.reportStatus('PATIENT_MEDIA_OK', 'Permisos OK, stream obtenido.');
          } else {
            this.hideCameraFlag = false;
            this.muteFlag = false;
          }

          this.userVideoSelected = data.idSocket;
          this.userVideoList.push({
            videollamada: stream,
            idSocket: data.idSocket,
            rtcPeerConnection: {},
            userName: this.usuario.use_name,
            userTypeId: this.usuario?.typ_id
          });

          this.logConnection('Se conectó a la videollamada (getUserMedia OK, joined)');

          this.startCall();

          const data_device = {
            roomName: this.consulta.id_consulta,
            codecs_audio: this.codecs_audio,
            codecs_video: this.codecs_video,
            codecs_audio_mimeType: this.codecs_audio_mimeType,
            codecs_video_mimeType: this.codecs_video_mimeType
          };

          this.web_rtc_socket.socketEmit('ready', data_device, null);
        })
        .catch(err => {
          console.log(err);

          if (this.usuario?.typ_id === 2) {
            // ✅ Esto alimenta tu observador()
            sessionStorage.setItem('zoom_ud_video', 'false');
            this.emitMediaPermissionError(err);
          } else {
            // doctor u otro rol
            this.general_services.modalError(
              'Error al generar video llamada',
              'No se puede acceder a los dispositivos. Verifique permisos y dispositivos.'
            );
          }
        });
    } catch (e) {
      if (this.usuario?.typ_id === 2) {
        sessionStorage.setItem('zoom_ud_video', 'false');
        this.emitMediaPermissionError(e);
        return;
      }

      this.general_services.modalError(
        'Error al generar video llamada',
        'No se puede acceder a los dispositivos, verifique permisos y dispositivos.'
      );
      this.router.navigate(['dashboard/'], { replaceUrl: true });
    }
  }

  created(data: any) {
    try {
      navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        .then(stream => {
          if (this.usuario?.typ_id === 2) {
            stream.getVideoTracks()[0].enabled = false;
            stream.getAudioTracks()[0].enabled = false;
            this.hideCameraFlag = true;
            this.muteFlag = true;
            this.reportStatus('PATIENT_MEDIA_OK', 'Permisos OK, stream obtenido.');
          } else {
            this.hideCameraFlag = false;
            this.muteFlag = false;
          }

          this.userVideoSelected = data.idSocket;
          this.userVideoList.push({
            videollamada: stream,
            idSocket: data.idSocket,
            rtcPeerConnection: {},
            userName: this.usuario.use_name,
            userTypeId: this.usuario?.typ_id
          });

          this.logConnection('Creó la sala y se conectó a la videollamada (getUserMedia OK, created)');
          this.startCall();
        })
        .catch(err => {
          console.log(err);

          if (this.usuario?.typ_id === 2) {
            sessionStorage.setItem('zoom_ud_video', 'false');
            this.emitMediaPermissionError(err);
            return;
          }

          this.general_services.modalError(
            'Error al generar video llamada',
            'No se puede acceder a los dispositivos. Verifique permisos y dispositivos.'
          );
          this.router.navigate(['dashboard/'], { replaceUrl: true });
        });
    } catch (e) {
      if (this.usuario?.typ_id === 2) {
        sessionStorage.setItem('zoom_ud_video', 'false');
        this.emitMediaPermissionError(e);
        return;
      }

      this.general_services.modalError(
        'Error al generar video llamada',
        'No se puede acceder a los dispositivos, verifique permisos y dispositivos.'
      );
      this.router.navigate(['dashboard/'], { replaceUrl: true });
    }
  }

  full(data: any) {
    alert('room is full, cant join');
  }

  participants(data: any) { }

  // ---------------------------
  // READY → creo peerConnection y oferto
  // ---------------------------
  ready(data: any) {
    let isCompatibleData: any = this.codecsCompatibility(data);

    if (isCompatibleData && isCompatibleData.array_audio && isCompatibleData.array_video) {
      let index = this.userVideoList.push({
        videollamada: null,
        idSocket: data.idSocket,
        rtcPeerConnection: new RTCPeerConnection(this.iceServers),
        userName: '',
        codecs_audio: isCompatibleData.array_audio,
        codecs_video: isCompatibleData.array_video,
        userTypeId: data.userTypeId
      });
      index--;

      if (data && data.idSocket && data.idSocket != this.userVideoList[0].idSocket) {
        this.userVideoSelected = data.idSocket;
      }

      const pc: RTCPeerConnection = this.userVideoList[index].rtcPeerConnection;

      pc.onconnectionstatechange = () => {
        if (this.usuario?.typ_id === 2) {
          const st = (pc as any).connectionState;
          if (st === 'connected') this.reportStatus('PATIENT_WEBRTC_CONNECTED', 'PeerConnection connected.');
          if (st === 'failed') this.reportStatus('PATIENT_WEBRTC_FAILED', 'PeerConnection failed.');
          if (st === 'disconnected') this.reportStatus('PATIENT_WEBRTC_FAILED', 'PeerConnection disconnected.');
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (this.usuario?.typ_id === 2) {
          const st = pc.iceConnectionState;
          if (st === 'connected' || st === 'completed') this.reportStatus('PATIENT_WEBRTC_CONNECTED', `ICE ${st}`);
          if (st === 'failed') this.reportStatus('PATIENT_WEBRTC_FAILED', 'ICE failed (posible red/turn).');
          if (st === 'disconnected') this.reportStatus('PATIENT_WEBRTC_FAILED', 'ICE disconnected (red inestable).');
        }
      };

      let data_set_codecs = {
        data: {
          codecs_audio: isCompatibleData.array_audio,
          codecs_video: isCompatibleData.array_video
        }
      };

      pc.onicecandidate = (event: any) => {
        if (event.candidate) {
          this.web_rtc_socket.socketEmit('candidate', ({
            roomName: this.consulta.id_consulta,
            idSocketFrom: this.userVideoList[0].idSocket,
            idSocketTo: data.idSocket,
            userName: this.usuario.use_name,
            userTypeId: this.usuario.typ_id,
            codecs_audio_mimeType: [isCompatibleData.array_audio[0]],
            codecs_video_mimeType: [isCompatibleData.array_video[0]],
          }), event.candidate);
        }
      };

      pc.ontrack = (event: any) => {
        if (event.streams && event.streams[0]) {
          this.userVideoList[index].videollamada = event.streams[0];
          this.logConnection('Recibido stream remoto (ontrack, ready)');
        }
      };

      // Agrega tracks locales (audio/video)
      pc.addTrack(this.userVideoList[0].videollamada.getTracks()[0], this.userVideoList[0].videollamada);
      pc.addTrack(this.userVideoList[0].videollamada.getTracks()[1], this.userVideoList[0].videollamada);

      this.setCodecs(index, data_set_codecs);

      pc.createOffer({ iceRestart: true })
        .then((offer: any) => {
          pc.setLocalDescription(offer);
          this.web_rtc_socket.socketEmit('offer', ({
            roomName: this.consulta.id_consulta,
            idSocketFrom: this.userVideoList[0].idSocket,
            idSocketTo: data.idSocket,
            userName: this.usuario.use_name,
            userTypeId: this.usuario.typ_id,
            codecs_audio_mimeType: this.codecs_audio_mimeType,
            codecs_video_mimeType: this.codecs_video_mimeType,
          }), offer);

          this.logConnection('Se enviará offer al otro participante (ready)');
          if (this.usuario?.typ_id === 2) this.reportStatus('PATIENT_WEBRTC_CONNECTING', 'Enviando offer / negociando.');
        })
        .catch((error: any) => {
          console.log(error);
        });

    } else {
      this.web_rtc_socket.socketEmit('isNotCompatible', ({
        roomName: this.consulta.id_consulta,
        idSocketFrom: this.userVideoList[0].idSocket,
        idSocketTo: data.idSocket,
        userName: this.usuario.use_name,
        userTypeId: this.usuario.typ_id,
      }), null);
    }
  }

  // ---------------------------
  // ICE candidate recibido
  // ---------------------------
  candidate(data: any) {
    let obj: any = data;
    if (typeof obj === 'string') obj = JSON.parse(data);

    const icecandidate = new RTCIceCandidate(obj.candidate);
    const index = this.userVideoList.findIndex(x => x.idSocket == obj.data.idSocketFrom);

    if (index === -1) return;

    if (obj.data.codecs_audio_mimeType && obj.data.codecs_video_mimeType) {
      let isCompatibleData: any = this.codecsCompatibility(obj.data);
      let data_set_codecs = {
        data: {
          codecs_audio: isCompatibleData.array_audio,
          codecs_video: isCompatibleData.array_video
        }
      };
      this.setCodecs(index, data_set_codecs);
    }

    this.userVideoList[index].rtcPeerConnection.addIceCandidate(icecandidate);
    this.userVideoList[index].userName = obj.data.userName;
    this.userVideoList[index].userTypeId = obj.data.userTypeId;

    try {
      const soyDoctor = this.usuario?.typ_id === 1;
      const esPacienteRemoto = obj.data.userTypeId === 2;

      if (soyDoctor && esPacienteRemoto && !this.patientConnectedLogged) {
        this.patientConnectedLogged = true;
        this.logConnection('✅ Detectado PACIENTE en signaling (candidate recibido).');
      }
    } catch (e) {
      console.warn('Error registrando log de conexión de paciente:', e);
    }
  }

  // ---------------------------
  // OFFER recibido → creo pc y respondo
  // ---------------------------
  offer(data: any) {
    let index = this.userVideoList.push({
      videollamada: null,
      idSocket: data.data.idSocketFrom,
      rtcPeerConnection: new RTCPeerConnection(this.iceServers),
      userName: data.data.userName,
      userTypeId: data.data.userTypeId
    });
    index--;

    if (this.userVideoList[index]?.idSocket) {
      this.userVideoSelected = this.userVideoList[index].idSocket;
    }

    const pc: RTCPeerConnection = this.userVideoList[index].rtcPeerConnection;

    pc.onconnectionstatechange = () => {
      if (this.usuario?.typ_id === 2) {
        const st = (pc as any).connectionState;
        if (st === 'connected') this.reportStatus('PATIENT_WEBRTC_CONNECTED', 'PeerConnection connected.');
        if (st === 'failed') this.reportStatus('PATIENT_WEBRTC_FAILED', 'PeerConnection failed.');
        if (st === 'disconnected') this.reportStatus('PATIENT_WEBRTC_FAILED', 'PeerConnection disconnected.');
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (this.usuario?.typ_id === 2) {
        const st = pc.iceConnectionState;
        if (st === 'connected' || st === 'completed') this.reportStatus('PATIENT_WEBRTC_CONNECTED', `ICE ${st}`);
        if (st === 'failed') this.reportStatus('PATIENT_WEBRTC_FAILED', 'ICE failed (posible red/turn).');
        if (st === 'disconnected') this.reportStatus('PATIENT_WEBRTC_FAILED', 'ICE disconnected (red inestable).');
      }
    };

    pc.onicecandidate = (event: any) => {
      if (event.candidate) {
        this.web_rtc_socket.socketEmit('candidate', ({
          roomName: this.consulta.id_consulta,
          idSocketFrom: this.userVideoList[0].idSocket,
          idSocketTo: data.data.idSocketFrom,
          userName: this.usuario.use_name,
          userTypeId: this.usuario.typ_id
        }), event.candidate);
      }
    };

    pc.ontrack = (event: any) => {
      if (event.streams && event.streams[0]) {
        this.userVideoList[index].videollamada = event.streams[0];

        const tipoRemoto =
          this.userVideoList[index].userTypeId === 1 ? 'DOCTOR' :
          this.userVideoList[index].userTypeId === 2 ? 'PACIENTE' :
          'DESCONOCIDO';

        this.logConnection(`Recibido video remoto de ${tipoRemoto} (ontrack)`);
      }
    };

    pc.addTrack(this.userVideoList[0].videollamada.getTracks()[0], this.userVideoList[0].videollamada);
    pc.addTrack(this.userVideoList[0].videollamada.getTracks()[1], this.userVideoList[0].videollamada);

    pc.setRemoteDescription(data.offer);

    pc.createAnswer()
      .then((answer: any) => {
        pc.setLocalDescription(answer);
        this.web_rtc_socket.socketEmit('answer', ({
          roomName: this.consulta.id_consulta,
          idSocketFrom: this.userVideoList[0].idSocket,
          idSocketTo: data.data.idSocketFrom,
        }), answer);

        if (this.usuario?.typ_id === 2) this.reportStatus('PATIENT_WEBRTC_CONNECTING', 'Recibió offer y envió answer.');
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  // ---------------------------
  // ANSWER recibido
  // ---------------------------
  answer(data: any) {
    let obj: any = data;
    if (typeof obj === 'string') obj = JSON.parse(data);

    let index = this.userVideoList.findIndex(x => x.idSocket == obj.data.idSocketFrom);
    if (index === -1) return;

    this.userVideoList[index].rtcPeerConnection.setRemoteDescription(obj.answer);
  }

  // ---------------------------
  // NOT COMPATIBLE / NOT AUTH
  // ---------------------------
  isNotCompatible(data: any) {
    this.isStartMeeting = false;
    let url = 'error-video';
    this.router.navigate([url], { replaceUrl: true });
    setTimeout(() => window.location.reload(), 500);
  }

  isNotAuthorized(data: any) {
    this.isStartMeeting = false;
    let url = 'dashboard';
    this.router.navigate([url], { replaceUrl: true });
    setTimeout(() => window.location.reload(), 500);
  }

  // ---------------------------
  // LEAVE del otro
  // ---------------------------
  leave(data: any) {
    let index = this.userVideoList.findIndex(x => x.idSocket == data.idSocket);

    try {
      this.userVideoSelected = this.userVideoSelected == data.idSocket
        ? this.userVideoList[0].idSocket
        : this.userVideoSelected;
    } catch (e) { }

    if (index != -1) {
      try {
        const leftUserType = this.userVideoList[index].userTypeId;
        const soyDoctor = this.usuario?.typ_id === 1;

        if (soyDoctor && leftUserType === 2 && !this.patientDisconnectedLogged) {
          this.patientDisconnectedLogged = true;
          this.logConnection(`⚠️ PACIENTE SALIÓ de la videollamada (socket: ${data.idSocket})`);
        }
      } catch (e) { }

      try {
        this.userVideoList[index].videollamada.getTracks()[0].stop();
        this.userVideoList[index].videollamada.getTracks()[1].stop();
        this.userVideoList[index].rtcPeerConnection.ontrack = null;
        this.userVideoList[index].rtcPeerConnection.onicecandidate = null;
        this.userVideoList[index].rtcPeerConnection.close();
        this.userVideoList[index].rtcPeerConnection = null;
      } catch (error) { }

      this.userVideoList.splice(index, 1);
      this.leaveSound.play();
    }
  }

  // ---------------------------
  // BOTONES
  // ---------------------------
  muteButtonFunction() {
    this.muteFlag = !this.muteFlag;
    if (this.userVideoList?.[0]?.videollamada?.getAudioTracks?.()?.[0]) {
      this.userVideoList[0].videollamada.getAudioTracks()[0].enabled = !this.muteFlag;
    }
  }

  hideButtonFunction() {
    this.hideCameraFlag = !this.hideCameraFlag;
    if (this.userVideoList?.[0]?.videollamada?.getVideoTracks?.()?.[0]) {
      this.userVideoList[0].videollamada.getVideoTracks()[0].enabled = !this.hideCameraFlag;
    }
  }

  leaveButtonFunction() {
    try {
      this.reportStatus('PATIENT_LEFT', 'El paciente salió/cerró la videollamada.');

      if (this.userVideoList[0]?.videollamada) {
        const tracks = this.userVideoList[0].videollamada.getTracks();
        tracks?.forEach((t: any) => t.stop());
      }

      if (this.userVideoList.length >= 2) {
        for (let i = 1; i < this.userVideoList.length; i++) {
          try {
            const tracks = this.userVideoList[i].videollamada?.getTracks?.() || [];
            tracks.forEach((t: any) => t.stop());
            this.userVideoList[i].rtcPeerConnection.ontrack = null;
            this.userVideoList[i].rtcPeerConnection.onicecandidate = null;
            this.userVideoList[i].rtcPeerConnection.close();
            this.userVideoList[i].rtcPeerConnection = null;
          } catch (e) { }
        }
      }

      this.userVideoList = [];
    } catch (error) { }

    this.endCall();
    sessionStorage.setItem('zoom_ud_video', 'false');
    sessionStorage.setItem('zoom_up_med', 'false');
  }

  // ---------------------------
  // START / END CALL
  // ---------------------------
  startCall() {
    this.isStartMeeting = true;

    if (this.usuario?.typ_id === 2) {
      this.reportStatus('PATIENT_WEBRTC_CONNECTING', 'Inició negociación WebRTC (postUpdateStartMeeting).');
    }

    this.logConnection('Inició negociación WebRTC (postUpdateStartMeeting)');

    this._zoomService.postUpdateStartMeeting({ id_consulta: this.consulta.id_consulta })
      .then(result => {
        // console.log(result, 'result-startCall');
      })
      .catch(error => console.log(error));
  }

  async endCall() {
    let url = '';

    if (this.isStartMeeting) {
      await this._zoomService.postUpdateEndMeeting({ id_consulta: this.consulta.id_consulta })
        .catch(error => console.log(error));

      if (this.usuario.typ_id == 1) {
        url = 'videoconsulta/' + this.consulta.id_consulta;
        this.router.navigate([url], { replaceUrl: true });
      } else if (this.usuario.typ_id == 2 && this.urlParams?.path == 'mivideoconsulta') {
        url = 'encuesta/' + this.consulta.med_con_id + '/' + this.consulta.id_consulta;
        this.router.navigate([url], { replaceUrl: true });
      } else {
        url = 'dashboard/';
        this.router.navigate([url], { replaceUrl: true });
        setTimeout(() => window.location.reload(), 500);
      }
    }
  }

  // ---------------------------
  // CODECS
  // ---------------------------
  codecsCompatibility(data: any) {
    let array_video: any[] = [];
    let array_audio: any[] = [];
    let find_audio_mimeType = false;
    let find_video_mimeType = false;

    for (let i = 0; i < data.codecs_audio_mimeType.length; i++) {
      if (!find_audio_mimeType) {
        const element = data.codecs_audio_mimeType[i];
        for (let j = 0; j < this.codecs_audio.length; j++) {
          const element_audio = this.codecs_audio[j];
          if (element_audio.mimeType == element.mimeType) {
            array_audio.push(element_audio);
            find_audio_mimeType = true;
          }
        }
      }
    }

    for (let i = 0; i < data.codecs_video_mimeType.length; i++) {
      if (!find_video_mimeType) {
        const element = data.codecs_video_mimeType[i];
        for (let j = 0; j < this.codecs_video.length; j++) {
          const element_video = this.codecs_video[j];
          if (element_video.mimeType == element.mimeType) {
            array_video.push(element_video);
            find_video_mimeType = true;
          }
        }
      }
    }

    if (array_audio.length == 0 || array_video.length == 0) {
      return false;
    }

    return { array_audio, array_video };
  }

  setCodecs(index: number, data: any) {
    if (!this.userVideoList[index].codecs_audio || this.userVideoList[index].codecs_audio.length == 0) {
      this.userVideoList[index].codecs_audio = data.data.codecs_audio;
      this.userVideoList[index].codecs_video = data.data.codecs_video;
    }

    let audio_index = 0;
    let video_index = 1;
    let transceiver = this.userVideoList[index].rtcPeerConnection.getTransceivers()[0];

    if (transceiver?.sender?.track?.kind) {
      if (transceiver.sender.track.kind == 'audio') {
        audio_index = 0;
        video_index = 1;
      } else {
        audio_index = 1;
        video_index = 0;
      }

      if (this.userVideoList[index].rtcPeerConnection.getTransceivers()[audio_index].setCodecPreferences != undefined) {
        this.userVideoList[index].rtcPeerConnection.getTransceivers()[audio_index]
          .setCodecPreferences(this.userVideoList[index].codecs_audio);
      }

      if (this.userVideoList[index].rtcPeerConnection.getTransceivers()[video_index].setCodecPreferences != undefined) {
        this.userVideoList[index].rtcPeerConnection.getTransceivers()[video_index]
          .setCodecPreferences(this.userVideoList[index].codecs_video);
      }
    }
  }

  // ---------------------------
  // FIN / PIN / UI
  // ---------------------------
  endMeeting() {
    if (this.usuario.typ_id == 1) {
      try {
        this.web_rtc_socket.socketEmit('endMeetingByDoctor', ({ roomName: this.consulta.id_consulta }), null);
      } catch (e) { }
    }
  }

  endMeetingByDoctor(data: any) {
    this.leaveButtonFunction();
  }

  pinUser(elementVideo: any) {
    this.userVideoSelected = elementVideo.idSocket;
  }

  cambiarPantallaVideo() {
    this.toggleView.emit();
    this.isVideoView = !this.isVideoView;
  }

  // ---------------------------
  // DESTROY + unload
  // ---------------------------
  ngOnDestroy() {
    if (this.patientConnectTimeoutRef) {
      clearTimeout(this.patientConnectTimeoutRef);
      this.patientConnectTimeoutRef = null;
    }
    this.leaveButtonFunction();
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: any) {
    this.leaveButtonFunction();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: any) {
    this.leaveButtonFunction();
  }
}
